import GameService from '~/services/game.service';
import * as inboxDb from '~/lib/indexedDb/inbox';
import { setLocalStore } from '~/lib/helpers';
import { clearGameplayData } from '~/lib/sessionCleanup';
import { TURN_PHASES } from '~/lib/constants';
import { NOT_FOUND } from '~/services/httpStatuses';
import { CreateTradeshowCustomer } from '~/models';
import { CharacterBuilder, CharacterPowerBuilder, ProductBuilder } from '~/models';
import { PRODUCT_TYPES } from '~/lib/constants';
import {
	turnActions, storeActions, uiActions, characterActions, productsActions,
	adActions, logbookActions, employeesActions, financialStatementsActions,
	levelActions, customerActions, carActions, warehouseActions, inboxActions,
	tradeshowsActions, playerHealthActions, checkpointActions
} from '~/store/actions';
import { gameSelectors, turnSelectors, levelSelectors } from '~/store/selectors';
import { isDevEnv } from '~/lib/helpers';
import { MAIN_SCREEN_MODALS } from '~/lib/constants';
// ---- internal imports -----

import { uiSelectors } from '../selectors';
import { createCustomersModels } from '../customers/utlis';
import { resetTimer } from '../turn/utlis';
import { LEVEL_STARTED_LEVEL_1 } from '../level/actionTypes';
// ---- parent imports ----

import * as types from './actionTypes';
import { getGamePlayStatsForSelectionsScreen } from './utlis';
// ---- local imports -----

function _getCharacterFromGameUser(gameUser) {
	const power = new CharacterPowerBuilder()
		.withTitle(gameUser.character_power)
		.withDescription(gameUser.character_power_info)
		.build();

	return new CharacterBuilder()
		.withId(gameUser.character_id)
		.withSlug(gameUser.character_slug)
		.withPower(power)
		.withCompletionMsg(gameUser.character?.completion_msg)
		.build();
}

function _getStoreFromGameUser(gameUser) {
	return {
		name: gameUser.store_name,
		rent: gameUser.store_rent,
		capacity: gameUser.store_capacity,
		location: gameUser.store_location
	};
}

function _mapProduct(product) {
	return new ProductBuilder()
		.withId(product.product_id)
		// TODO: remove product name or type from backend and only keep one of them, 
		// currently, product name is used as product type.
		.withType(product.product_name)
		.withAmount(product.amount)
		.withSellingPrice(product.selling_price)
		.withInitialSellingPrice(product.product_original_price)
		.withOriginalPurchasingPrice(product.product_original_price)
		.withPurchasingPrice(product.product_price)
		.build();
}

/**
 * Lets go of a save that cannot be opened, so the player is not sent back to it.
 *
 * Until a logout stopped taking the playthrough with it, `localStorage.clear()`
 * removed `current-game-id` on every 401 and this could not arise. Now the key
 * survives, and a save that will not load is a closed loop: `Loader` reads the
 * key on mount, sends the player to `/game/game-user`, the load fails, nothing
 * clears the key — and the next reload does it all again, with no way out from
 * inside the app.
 *
 * The whole playthrough goes, not just the pointer. What is on disk belongs to a
 * game this player cannot open, and that includes the inbox — which is keyed by
 * message id with no game or player in it, so leaving it behind is how the
 * previous player's mail is read by the next one.
 *
 * Awaited, unlike the call in `resetGame`: nothing here navigates on the next
 * line, so the IndexedDB clear is allowed to finish before the reload that
 * would otherwise cut it off.
 */
async function _recoverFromUnloadableSave(dispatch, status) {
	// A 403 is the interceptor's: it lets the save go from any request, and has
	// done so before this thunk is handed the result.
	if (status !== NOT_FOUND) {
		return;
	}

	await clearGameplayData();
	dispatch({ type: types.GAME_RESET });

	// A full load rather than a route change: this is recovery from a game screen
	// that was mounted for a save that never arrived, and `Loader` re-reads the
	// key on mount — with it gone, it sends the player on to the same place.
	window.location.href = '/game/select-character';
}

export function onRestartGameClick() {
	return async (dispatch, getState) => {
		dispatch(uiActions.setPreviouslyActiveModal(uiSelectors.getActiveModal(getState())));
		dispatch(uiActions.changeActiveModal(MAIN_SCREEN_MODALS.RESTART_GAME_CONFIRMATION));
	};
}

export function resetGame(shouldRefresh = false) {
	return async (dispatch) => {
		// The list lives in `~/lib/sessionCleanup` rather than here, so the keys a
		// restart drops are written down in one place. It also clears the inbox
		// now, which the hand-written list here never did — the messages used to
		// sit in IndexedDB until the next `createNew` or `load` wiped them.
		//
		// Deliberately not awaited: the storage keys are dropped synchronously
		// inside the call, and callers navigate on the line after dispatching this
		// (`RestartGameConfirmationModal`), so awaiting the IndexedDB transaction
		// would push `GAME_RESET` behind that navigation. The clear finishes on its
		// own, and `createNew`/`load` clear the inbox again regardless.
		clearGameplayData();

		if (!shouldRefresh) {
			dispatch({ type: types.GAME_RESET });
		}
	};
}

export function createNew(userId, characterId, storeId) {
	return async (dispatch) => {
		try {
			dispatch({ type: types.GAME_CREATED_NEW_START });

			const selectionTiming = getGamePlayStatsForSelectionsScreen();
			const result = await GameService.createNew(userId, characterId, storeId, selectionTiming);
			if (!result.isSuccess) {
				console.warn("REQUEST FAILED: GameService.createNew");
				return false;
			}

			const gameUser = result.data;

			setLocalStore('screen', 'store');
			setLocalStore('current-game-id', gameUser.id);
			setLocalStore('automatedEmployees', {});
			setLocalStore('dontShowMissoutOnCustomersModal', {});
			resetTimer();
			inboxDb.clearMessages();

			dispatch({ type: types.GAME_CREATED_NEW, gameUser });
			dispatch(employeesActions.getAllEmployees())
				.then(() => dispatch(employeesActions.getHiredEmployees()));
			dispatch(characterActions.characterLoaded(_getCharacterFromGameUser(gameUser)));
			dispatch(turnActions.updateByGameUser(gameUser));
			dispatch(storeActions.storeLoaded(_getStoreFromGameUser(gameUser)));
			dispatch(productsActions.productsLoaded(mapProducts(gameUser.products)));

			dispatch(adActions.fetchAvailableAds());
			dispatch(levelActions.setUpFirstLevelForNewGame());

			dispatch(uiActions.setUpUiForNewGame());
dispatch(logbookActions.logbookLoaded(
				gameUser.logbook, gameUser.system_log, gameUser.is_logbook_automated_by_date
			));
			dispatch(warehouseActions.fetchAllWarehouses());
			dispatch(carActions.fetchAllCars());
			dispatch(tradeshowsActions.fetchAll());
			dispatch(playerHealthActions.load(gameUser.player_health ?? 0));

			if (window.heap) {
				window.heap.addUserProperties({ latestGameUserId: gameUser.id });
				window.heap.addEventProperties({ gameUserId: gameUser.id });
			}

			dispatch({ type: types.GAME_CREATED_NEW_ENDED });
			return true;
		} catch (error) {
			console.error(error);
		}
	};
}

export function load(gameUserId, loadedForCheckpoint = false) {
	return async (dispatch, getState) => {
		try {
			setLocalStore('screen', 'store');
			localStorage.setItem('gamePlayTime', JSON.stringify({
				active: 0,
				idle: 0,
			  }));

			dispatch({ type: types.GAME_LOADED_START });

			const result = await GameService.load(gameUserId);
			if(!result.isSuccess) {
				console.warn('REQUEST FAILED: GameService.load');
				await _recoverFromUnloadableSave(dispatch, result.status);
				return;
			}

			const gameUser = result.data;

			dispatch(inboxActions.fetchAllMessages(gameUserId));
			dispatch(employeesActions.loadAutomatedEmployees());
			inboxDb.clearMessages();

			dispatch({ type: types.GAME_LOADED, gameUser });
			dispatch(characterActions.characterLoaded(_getCharacterFromGameUser(gameUser)));

			if (window.heap) {
				window.heap.addUserProperties({ latestGameUserId: gameUser.id });
				window.heap.addEventProperties({ gameUserId: gameUser.id });
			}

			dispatch(employeesActions.getAllEmployees())
				.then(() => dispatch(employeesActions.getHiredEmployees()));

			dispatch(turnActions.updateByGameUser(gameUser));
			dispatch(storeActions.storeLoaded(_getStoreFromGameUser(gameUser)));
			dispatch(productsActions.productsLoaded(mapProducts(gameUser.products)));
			dispatch(adActions.fetchBoughtAds());
			dispatch(adActions.fetchAvailableAds());

			dispatch(logbookActions.logbookLoaded(
				gameUser.logbook, gameUser.system_log, gameUser.is_logbook_automated_by_date
			));
			dispatch(financialStatementsActions.financialStatementsLoad());
			dispatch(levelActions.loadLevel(
				gameUser, { missions: gameUser.missions, level: gameUser.level }, gameUser.checkpoint_reload_index
			));

			dispatch(warehouseActions.fetchAllWarehouses())
				.then(() => dispatch(warehouseActions.fetchLeasedWarehouses()));

			dispatch(carActions.fetchAllCars())
				.then(() => dispatch(carActions.fetchLeasedCars()));

			dispatch(tradeshowsActions.fetchAll())
				.then(() => dispatch(tradeshowsActions.fetchHiredBooths()));

			dispatch(uiActions.setupUiAfterReload(gameUser));

			dispatch(playerHealthActions.load(gameUser.player_health ?? 0));

			if (turnSelectors.getTurnPhase(getState()) === TURN_PHASES.CUSTOMERS) {
				dispatch(customerActions.receivedTurnCustomers(gameUser.customers));
				const waitingTradeshowCustomers = gameUser.tradeshow_customers.filter(customer => customer.state !== 'sold')
				const hasNotFinsihedTradeshow = waitingTradeshowCustomers.length > 0;

				if (hasNotFinsihedTradeshow) {
					dispatch(tradeshowsActions.start(
						gameUser.tradeshow_customers.map(CreateTradeshowCustomer)
					));
				} else {
					setLocalStore('screen', 'store');
					dispatch(productsActions.productsSalesDataLoaded(createCustomersModels(gameUser.customers)));
					dispatch(customerActions.loadCustomers(gameUser.customers));
				}
			}

			if (loadedForCheckpoint) {
				dispatch(uiActions.resetDisplayedCashUpdates());	// remove cash udpates before checkpoint is reload
				dispatch(uiActions.resetPowerModalsAfterLevelUp(gameUser.level));
				if (gameUser.level === 1) {
					dispatch({ type: LEVEL_STARTED_LEVEL_1 });
				} else {
					dispatch(uiActions.showNewLevelModal());	// level one has no new level modal
				}
				dispatch(checkpointActions.reloaded());
				dispatch(customerActions.skipCurrentCustomers());
			}

			dispatch({
				type: types.GAME_LOADED_ENDED,
				playerHealth: gameUser.player_health,	//	added to display GameOver
				cash: gameUser.cash,	//	added to display GameOver
			});

		} catch (error) {
			console.error(error);
		}
	};
}

export function incrementCash(amount) {
	return async (dispatch, getState) => {
		const updatedCash = gameSelectors.getCash(getState()) + amount;
		// Note that the value is used for missions
		dispatch({ type: types.GAME_UPDATE_CASH, value: amount, updatedCash });
	};
}

export function updateCash(newCash) {
	return async (dispatch, getState) => {
		const cashDifference = newCash - gameSelectors.getCash(getState());
		// Note that the value is used for missions
		dispatch({ type: types.GAME_UPDATE_CASH, value: cashDifference, updatedCash: newCash });
	};
}

export function updateStartedTurnWithAutoBookkeeper(hasStartedTurnWithAutoBookkeeper) {
	return {
		type: types.GAME_UPDATE_STARTED_TURN_WITH_AUTO_BOOKKEEPER,
		hasStartedTurnWithAutoBookkeeper
	};
}

export function mapProducts(products) {
	const couch = products.find((p) => p.product_name === PRODUCT_TYPES.COUCH);
	const chair = products.find((p) => p.product_name === PRODUCT_TYPES.CHAIR);
	const table = products.find((p) => p.product_name === PRODUCT_TYPES.TABLE);

	return {
		couch: _mapProduct(couch),
		chair: _mapProduct(chair),
		table: _mapProduct(table)
	};
}

export function dangerousUpdateCashFromDevTools(newCash) {
	return async (dispatch, getState) => {
		if (!isDevEnv()) {
			return;
		}

		const result = await GameService.saveNewCash(gameSelectors.getGameId(getState()), newCash);

		if (!result.isSuccess) {
			return;
		}

		dispatch({ type: types.GAME_UPDATE_CASH, updatedCash: parseInt(newCash) });
	};
}

